import { Cookies } from "quasar";
import { isSupportedLocale, loadLocaleMessages } from "src/i18n";
import { LocaleKey } from "@/consts";
import type { ILocales } from "./types";
import { addDateByDays } from "src/utils/addDateByDays";
import { computed, useSSRContext } from "vue";
import { useI18n } from "vue-i18n";
export const useLang = () => {
  const defaultLocale: string = process.env.APP_DEFAULT_LOCALE || "en";
  const availableLocales: ILocales[] = [
    {
      name: "English",
      iso: "en",
      flag: "🇺🇸",
    },
    {
      name: "Tiếng Việt",
      iso: "vi",
      flag: "TH",
    },
    {
      name: "Spanish",
      iso: "es",
      flag: "ES",
    },
    {
      name: "China",
      iso: "zh",
      flag: "ZH",
    },
  ];
  const i18n = useI18n({ useScope: "global" });
  const { t, locale } = i18n;
  const ssrContext = process.env.SERVER ? useSSRContext() : null;
  const cookies = process.env.SERVER ? Cookies.parseSSR(ssrContext) : Cookies;
  const localeCookie = computed(() => cookies.get(LocaleKey) || defaultLocale);
  const currenLocale = computed(() =>
    availableLocales.find((t) => t.iso == locale.value),
  );
  const currentLocaleIso = computed(() => currenLocale.value?.iso ?? "en");

  const currentLangugeName = computed(() => {
    const l = availableLocales.find((t) => t.iso == locale.value);
    return l != undefined && l?.name ? l?.name : "unknown";
  });

  const setLocaleCookie = (lang: string) => {
    cookies.set(LocaleKey, lang, {
      // maxAge: 60 * 60 * 24 * 365 * 5,
      expires: addDateByDays(365),
      path: "/",
    });
    locale.value = lang;
  };
  // Lazy: nạp messages của locale đích nếu chưa có. Trả về true nếu locale đã
  // sẵn sàng để đổi; false nếu locale không hợp lệ hoặc load chunk lỗi (mạng,
  // asset…) → KHÔNG đổi, tránh flash raw key và tránh unhandled rejection.
  const ensureLocaleLoaded = async (lang: string): Promise<boolean> => {
    if (!isSupportedLocale(lang)) return false;
    if (i18n.availableLocales.includes(lang)) return true;
    try {
      i18n.setLocaleMessage(lang, await loadLocaleMessages(lang));
      return true;
    } catch (e) {
      console.error(`[i18n] Failed to load locale "${lang}"`, e);
      return false;
    }
  };
  const onSetLocale = async (lang: string) => {
    if (await ensureLocaleLoaded(lang)) {
      setLocaleCookie(lang);
    }
  };
  const initialLocale = async () => {
    const lang = isSupportedLocale(localeCookie.value)
      ? localeCookie.value
      : "en";
    if (await ensureLocaleLoaded(lang)) {
      setLocaleCookie(lang);
    }
  };
  return {
    t,
    locale,
    currenLocale,
    availableLocales,
    currentLangugeName,
    onSetLocale,
    initialLocale,
    defaultLocale,
    currentLocaleIso,
  };
};
